<template>
  <div>
    <apex-chart-card
      v-if="hasData"
      chart-title="Histórico de indicadores x Disparos"
      :filter-by-date-period.sync="filterByDatePeriod"
      :config-update-options="configUpdateOptions"
      :chart-loading="$apollo.queries.generalMetricsKpiRevenueHistory.loading || $apollo.queries.metricsKpiRevenueHistory.loading || loading"
      :chart-data="metricsKpiRevenueHistory"
      :chart-colors="chartColors"
      :series-data="seriesData"
      :current-chart-data.sync="currentChartData"
      hide-chart-type-button
      is-stacked
    >
      <template #tabs-menu>
        <mf-select v-model="selectedChart" style="width: 246px" hide-details :items="chartOptions" label="" item-text="text" item-value="value" dense />
      </template>
    </apex-chart-card>
    <v-card-title v-if="!hasData" class="pt-5 pl-6">
      <v-container class="pa-0 pr-1" fluid fill-height>
        <h2 class="table-card-title">Histórico de indicadores x Disparos</h2>
      </v-container>
    </v-card-title>
    <empty-chart v-if="!hasData" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { DateTime } from 'luxon'

import ApexChartCard from '@/components/ApexChartCard.vue'
import { localeString, abbreviation } from '@/config/helpers.js'
import { QUERY_GET_METRICS } from '@/modules/maria/campanha-da-maria-v2/graphql'
import EmptyChart from '@/modules/mobile/desempenho/views/components/EmptyChart'
import { mariaResults } from '@/modules/maria/campanha-da-maria-v2/mixins/maria-results'

import globalStyles from '@/assets/styles/global.scss'

export default {
  name: 'ProductBillingHistory',
  components: {
    ApexChartCard,
    EmptyChart,
  },
  mixins: [mariaResults],
  props: {
    units: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    filterByDatePeriod: 'diario',
    locale: localeString,
    metricsKpiRevenueHistory: [],
    generalMetricsKpiRevenueHistory: [],
    currentChartData: [],
    products: [],
    chartColors: [globalStyles['marca-0'], globalStyles['marca-1'], globalStyles['marca-0-l40']],
    selectedChart: 'revenue',
    tooltipCache: new Map(),
    dataPointCache: new Map(),
    memoizedTooltipFunction: null,
    dateToDataMap: new Map(),
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),
    chartOptions() {
      return [
        {
          text: 'Faturamento',
          value: 'revenue',
        },
        {
          text: 'Cupons',
          value: 'total_coupons',
        },
        {
          text: 'Clientes que compraram',
          value: 'unique_customers',
        },
      ]
    },
    productIds() {
      if (!this.campaign.offer_sales || !this.campaign.offer_sales.length) return []

      return this.campaign.offer_sales.map((offer) => offer.sku)
    },
    hasData() {
      return (
        this.metricsKpiRevenueHistory.reduce((total, valor) => total + valor.campaignRevenues, 0) +
        this.metricsKpiRevenueHistory.reduce((total, valor) => total + valor.generalRevenues, 0)
      )
    },
    skipRevenueHistory() {
      return !this.campaign._id || this.$apollo.queries.generalMetricsKpiRevenueHistory.loading || !this.generalMetricsKpiRevenueHistory.length
    },
    defaultQueryVariables() {
      const variables = {
        initialDate: this.campaign.initial_date,
        finalDate: this.campaign.final_date,
        groupBy: this.queryGroupFilterByDatePeriod,
      }
      return variables
    },
    queryGroupFilterByDatePeriod() {
      switch (this.filterByDatePeriod) {
        case 'semanal':
          return 'WEEK'
        case 'diario':
          return 'DAY'
        default:
          return 'MONTH'
      }
    },
    tooltipConfig() {
      return {
        shared: true,
        intersect: false,
        custom: this.memoizedTooltipFunction || this.createTooltipContent,
      }
    },
    configUpdateOptions() {
      const config = {
        tooltip: this.tooltipConfig,
        legend: {
          horizontalAlign: 'left',
          position: 'bottom',
          fontSize: '14px',
          markers: {
            shape: 'square',
          },
          offsetX: 25,
          itemMargin: {
            horizontal: 15,
          },
        },
        dataLabels: {
          enabled: false,
        },
        markers: {
          size: 5,
          strokeWidth: 0,
        },
        yaxis: [
          {
            seriesName: this.seriesData[1].name,
            labels: {
              formatter: this.yaxisFormatter,
            },
          },
          {
            seriesName: this.seriesData[1].name,
            show: false,
          },
          {
            opposite: true,
            seriesName: 'Quantidade de disparos',
            labels: {
              formatter: (value) => {
                return this.localeString(value)
              },
            },
          },
        ],
      }

      return config
    },
    yaxisFormatter() {
      return (value) => {
        switch (this.selectedChart) {
          case 'total_coupons':
            return this.localeString(value)
          case 'unique_customers':
            return this.localeString(value)
          case 'revenue':
          default:
            return `R$ ${abbreviation(value, { isCurrency: true })}`
        }
      }
    },
    revenueSeriesData() {
      return [
        {
          name: 'Faturamento total',
          chartDataPropertyName: 'generalRevenues',
          type: 'bar',
        },
        {
          name: 'Faturamento das campanhas',
          chartDataPropertyName: 'campaignRevenues',
          type: 'bar',
        },
      ]
    },
    couponsSeriesData() {
      return [
        {
          name: 'Total de cupons',
          chartDataPropertyName: 'generalCoupons',
          type: 'bar',
        },
        {
          name: 'Total de cupons das campanhas',
          chartDataPropertyName: 'campaignCoupons',
          type: 'bar',
        },
      ]
    },
    clientsSeriesData() {
      return [
        {
          name: 'Total de clientes',
          chartDataPropertyName: 'generalClients',
          type: 'bar',
        },
        {
          name: 'Total de clientes das campanhas',
          chartDataPropertyName: 'campaignClients',
          type: 'bar',
        },
      ]
    },
    seriesData() {
      const dispatchSeries = {
        name: 'Quantidade de disparos',
        chartDataPropertyName: 'generalMessages',
        type: 'line',
      }
      switch (this.selectedChart) {
        case 'total_coupons':
          return this.couponsSeriesData.concat(dispatchSeries)
        case 'unique_customers':
          return this.clientsSeriesData.concat(dispatchSeries)
        case 'revenue':
        default:
          return this.revenueSeriesData.concat(dispatchSeries)
      }
    },
  },
  apollo: {
    generalMetricsKpiRevenueHistory: {
      query: QUERY_GET_METRICS,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      update({ metricsKpi }) {
        const { revenue, total_coupons, unique_customers } = metricsKpi
        return revenue.map((revenue, index) => {
          return {
            revenue: revenue?.value,
            coupons: total_coupons[index]?.value,
            clients: unique_customers[index]?.value,
            label: [DateTime.fromFormat(revenue.date[0], 'yyyy-MM-dd').toFormat('dd/MM/yyyy')],
          }
        })
      },
      variables() {
        return {
          initialDate: this.campaign.initial_date.slice(0, -2).concat('01'),
          finalDate: this.campaign.final_date,
          campaignIds: [],
          units: this.units,
          groupBy: this.queryGroupFilterByDatePeriod,
          productIds: this.productIds,
          useMessagesCampaignsMetrics: true,
        }
      },
      skip() {
        return !this.defaultQueryVariables || !this.defaultQueryVariables.initialDate || !this.defaultQueryVariables.finalDate
      },
      context() {
        return {
          uri: this.$microservicesUrls['campaigns'],
        }
      },
    },
    metricsKpiRevenueHistory: {
      query: QUERY_GET_METRICS,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      update({ metricsKpi }) {
        const { total_coupons, revenue, unique_customers, messages_campaigns } = metricsKpi
        const generalRevenues = this.generalMetricsKpiRevenueHistory.slice(
          this.generalMetricsKpiRevenueHistory.findIndex((metric) => {
            return metric.label[0] === DateTime.fromFormat(revenue[0].date[0], 'yyyy-MM-dd').toFormat('dd/MM/yyyy')
          })
        )

        const generalSentMessagesMap = new Map()
        const emailSentMessagesMap = new Map()
        const smsSentMessagesMap = new Map()
        const pushSentMessagesMap = new Map()
        const whatsappSentMessagesMap = new Map()

        messages_campaigns?.general?.sent?.forEach((message) => {
          generalSentMessagesMap.set(message.date[0], message.value)
        })
        messages_campaigns?.channels?.email?.sent?.forEach((message) => {
          emailSentMessagesMap.set(message.date[0], message.value)
        })
        messages_campaigns?.channels?.sms?.sent?.forEach((message) => {
          smsSentMessagesMap.set(message.date[0], message.value)
        })
        messages_campaigns?.channels?.push?.sent?.forEach((message) => {
          pushSentMessagesMap.set(message.date[0], message.value)
        })
        messages_campaigns?.channels?.whatsapp?.sent?.forEach((message) => {
          whatsappSentMessagesMap.set(message.date[0], message.value)
        })

        return revenue.map((revenue, index) => {
          const currentDate = revenue.date[0]
          const pushValue = pushSentMessagesMap.get(currentDate) || 0
          const emailValue = emailSentMessagesMap.get(currentDate) || 0
          const smsValue = smsSentMessagesMap.get(currentDate) || 0
          const whatsappValue = whatsappSentMessagesMap.get(currentDate) || 0
          const generalValue = generalSentMessagesMap.get(currentDate) || 0

          return {
            campaignRevenues: revenue?.value,
            generalRevenues: generalRevenues[index]?.revenue,
            campaignCoupons: total_coupons[index]?.value,
            generalCoupons: generalRevenues[index]?.coupons,
            campaignClients: unique_customers[index]?.value,
            generalClients: generalRevenues[index]?.clients,
            totalSent: total_coupons[index]?.value,
            generalMessages: generalValue,
            emailMessages: emailValue,
            smsMessages: smsValue,
            pushMessages: pushValue,
            whatsappMessages: whatsappValue,
            label:
              revenue.date.length === 1
                ? [DateTime.fromFormat(revenue.date[0], 'yyyy-MM-dd').toFormat('dd/MM/yyyy')]
                : [
                    DateTime.fromFormat(revenue.date[0], 'yyyy-MM-dd').toFormat('dd/MM/yyyy'),
                    DateTime.fromFormat(revenue.date[1], 'yyyy-MM-dd').toFormat('dd/MM/yyyy'),
                  ],
          }
        })
      },
      variables() {
        return {
          initialDate: this.campaign.initial_date,
          finalDate: this.campaign.final_date,
          campaignIds: [this.campaign._id],
          units: this.units,
          groupBy: this.queryGroupFilterByDatePeriod,
          productIds: this.productIds,
          useMessagesCampaignsMetrics: true,
        }
      },
      skip() {
        return this.skipRevenueHistory
      },
      context() {
        return {
          uri: this.$microservicesUrls['campaigns'],
        }
      },
    },
  },
  watch: {
    productIds: {
      deep: true,
      handler: function (newValue) {
        if (!this.skipRevenueHistory) {
          this.products = newValue
          this.clearCache()
          const variables = {
            ...this.defaultQueryVariables,
            campaignIds: [this.campaign._id],
            productIds: this.products,
          }
          this.$apollo.queries.metricsKpiRevenueHistory.refetch({
            ...variables,
          })
        }
      },
    },
    selectedChart() {
      this.clearCache()
    },
    filterByDatePeriod() {
      this.clearCache()
    },
    currentChartData: {
      handler() {
        this.clearCache()
      },
      deep: true,
    },
    metricsKpiRevenueHistory: {
      handler() {
        this.dateToDataMap.clear()
      },
      deep: true,
    },
  },
  mounted() {
    this.memoizedTooltipFunction = this.createTooltipContent.bind(this)
  },
  beforeDestroy() {
    this.clearCache()
  },
  methods: {
    clearCache() {
      this.tooltipCache.clear()
      this.dataPointCache.clear()
      this.dateToDataMap.clear()
      this.memoizedTooltipFunction = this.createTooltipContent.bind(this)
    },
    formatRevenueForTooltip(value) {
      return `R$ ${this.localeString(value)}`
    },
    createTooltipContent({ series, dataPointIndex, w }) {
      const cacheKey = `${dataPointIndex}-${this.selectedChart}-${this.queryGroupFilterByDatePeriod}`

      if (this.tooltipCache.has(cacheKey)) {
        return this.tooltipCache.get(cacheKey)
      }

      const colors = w.config.colors
      const { newChartLabels } = this.currentChartData
      let periodTitle = newChartLabels[dataPointIndex]
      const generalData = series[0]
      const campaignData = series[1]
      const dispatchData = series[2]
      const campaignDataByPeriod = campaignData[dataPointIndex]
      const dispatchByPeriod = dispatchData[dataPointIndex]

      const periodTitleValue = Array.isArray(periodTitle) ? periodTitle[0] : periodTitle

      let currentDataPoint
      const dataPointKey = `${periodTitleValue}-${this.selectedChart}`

      if (this.dataPointCache.has(dataPointKey)) {
        currentDataPoint = this.dataPointCache.get(dataPointKey)
      } else {
        currentDataPoint = this.findDataPointByPeriodTitle(periodTitleValue)
        this.dataPointCache.set(dataPointKey, currentDataPoint)
      }

      const emailMessages = currentDataPoint?.emailMessages || 0
      const smsMessages = currentDataPoint?.smsMessages || 0
      const pushMessages = currentDataPoint?.pushMessages || 0
      const whatsappMessages = currentDataPoint?.whatsappMessages || 0

      const yaxisFormatter = this.selectedChart === 'revenue' ? this.formatRevenueForTooltip : w.config.yaxis[0].labels.formatter

      let displayPeriodTitle = periodTitle
      if (this.queryGroupFilterByDatePeriod === 'WEEK' && Array.isArray(periodTitle)) {
        displayPeriodTitle = `${periodTitle[0]} até ${periodTitle[1]}`
      }

      const tooltipContent = `
            <v-card class="py-2">
              <v-card-text>
                <div class="px-4 tooltip-container">
                  <span class="tooltip-title">${displayPeriodTitle}</span>

                  <div class="tooltip-description-value">
                    <span class="description-text">${w.config.series[0].name}:</span>
                    <span class="value-text" style="color: ${colors[0]}">${yaxisFormatter(generalData[dataPointIndex])}</span>
                  </div>

                  <div class="tooltip-description-value">
                    <span class="description-text">${w.config.series[1].name}:</span>
                    <span class="value-text" style="color: ${colors[1]}">${yaxisFormatter(campaignDataByPeriod)}</span>
                  </div>

                  <div class="tooltip-channels">
                    <div class="tooltip-description-value channels-title">
                      <span class="description-text">Disparos realizados:</span>
                      <span class="value-text">${this.locale(dispatchByPeriod)}</span>
                    </div>

                    <div v-if="${this.isChannelActivated('sms')}" class="tooltip-description-value">
                      <span class="channel-sms">SMS:</span>
                      <span class="channel-value">${this.locale(smsMessages)}</span>
                    </div>

                    <div v-if="${this.isChannelActivated('whatsapp')}" class="tooltip-description-value">
                      <span class="channel-whatsapp">Whatsapp:</span>
                      <span class="channel-value">${this.locale(whatsappMessages)}</span>
                    </div>

                    <div v-if="${this.isChannelActivated('push')}" class="tooltip-description-value">
                      <span class="channel-push">Push Aplicativo:</span>
                      <span class="channel-value">${this.locale(pushMessages)}</span>
                    </div>

                    <div v-if="${this.isChannelActivated('email')}" class="tooltip-description-value">
                      <span class="channel-email">E-mail:</span>
                      <span class="channel-value">${this.locale(emailMessages)}</span>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          `

      this.tooltipCache.set(cacheKey, tooltipContent)
      return tooltipContent
    },
    findDataPointByPeriodTitle(periodTitle) {
      if (this.dateToDataMap.size === 0) {
        this.createDateMapping()
      }

      return this.dateToDataMap.get(periodTitle) || this.metricsKpiRevenueHistory[0]
    },
    createDateMapping() {
      this.dateToDataMap.clear()

      this.metricsKpiRevenueHistory.forEach((dataPoint, index) => {
        const originalDate = dataPoint.label[0]

        let formattedKey
        if (this.queryGroupFilterByDatePeriod === 'MONTH') {
          const date = DateTime.fromFormat(originalDate, 'dd/MM/yyyy')
          formattedKey = date.toFormat('MMMM/yyyy')
        } else if (this.queryGroupFilterByDatePeriod === 'WEEK') {
          formattedKey = originalDate
        } else {
          formattedKey = originalDate
        }

        this.dateToDataMap.set(formattedKey, dataPoint)
      })
    },
  },
}
</script>
<style lang="scss" scoped>
::v-deep .tooltip-container {
  display: flex;
  flex-direction: column;
  gap: 2px;

  .tooltip-title {
    font-weight: 600;
    font-size: 14px;
    color: $cinza-d20;
  }

  .tooltip-description-value {
    display: flex;
    flex-direction: row;
    gap: 4px;
    align-items: center;

    .description-text {
      font-weight: normal;
      font-size: 12px;
      color: $cinza-0;
    }

    .value-text {
      font-weight: bold;
      font-size: 14px;
    }
  }

  .tooltip-channels {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-end;
    width: fit-content;
    font-weight: normal;
    font-size: 12px;

    .channels-title {
      justify-self: flex-start;
    }

    .channel-sms {
      color: #f36600;
    }

    .channel-whatsapp {
      color: $verde-0;
    }

    .channel-push {
      color: #801de3;
    }

    .channel-email {
      color: #0063c6;
    }

    .channel-value {
      color: $cinza-d10;
    }
  }
}

::v-deep .apexcharts-legend {
  .apexcharts-legend-series {
    &:last-child {
      .apexcharts-legend-marker {
        border-radius: 50% !important;
      }
    }
  }
}
</style>
